import React from 'react';
import { Link } from 'react-router-dom';
import { TOOLS, BUILD_SECTION, START_FREE } from '../../../content/landingHome';
import { cost } from '../../../data/patentLabEvidence';
import {
    InterviewCard, SheetCard, ReviewCard, PresenterCard, BUILD_ARTIFACT_CAPTIONS,
} from '../PatentLabProof';
import { Eyebrow, Source, StartFree, Arrow, Badge } from './parts';

// The four Build tools on the landing page: the strip near the top (all four
// inside the first phone screen and a half) and the full cards further down.
// Counts in the copy ("four tools") are counted from what is shown.

const ARTIFACTS = { brainstorm: InterviewCard, patent: SheetCard, research: ReviewCard, pitch: PresenterCard };

// The sourced attorney estimate (tag S4 in data/patentLabEvidence.js, pinned
// to the research record by patentLabEvidence.test.js).
const ATTORNEY = cost.tiles.find((t) => t.tag === 'S4');

// The Build set grows (founder, 2026-09-29: "our builder portfolio will only
// expand with more tools"). `more` adds a quiet, non-interactive "More on the
// way" tile after the tools, with no names or dates. The grids take a fifth
// or sixth tool without a redesign: the strip is two columns on a phone (a
// new tool adds a row) and auto-fits tiles of at least 170px wide; the cards
// are two columns on a tablet and three wide, so six tools are two rows.
// `section` is the BUILD_SECTION copy (landingHome.js, or a mock variant's).
function MoreTile({ copy, big = false }) {
    // In the strip: one more tile (a full row on a phone). Under the cards: a
    // low full-width row, never an equal-height card (design review: a tall,
    // mostly empty dashed card read as unfinished).
    return (
        <li className={`min-w-0 ${big ? 'col-span-full' : 'col-span-2 lg:col-span-1'}`} data-testid="landing-more-tools">
            <div className={`flex h-full rounded-2xl border-2 border-dashed border-[#CFC9BA] ${big ? 'flex-col items-center justify-center px-4 py-5 text-center sm:flex-row sm:gap-3' : 'flex-col justify-start px-3.5 py-2.5 lg:p-3.5'}`}>
                <span className="font-serif text-lg font-bold leading-tight text-[#4A4A4A]">{copy.title}</span>
                <span className={`text-sm leading-snug text-[#4A4A4A] ${big ? 'mt-1 sm:mt-0' : 'mt-1'}`}>{copy.body}</span>
            </div>
        </li>
    );
}

export function BuildStrip({ tools, section = BUILD_SECTION, more = null }) {
    if (tools.length === 0) return null;
    const body = (
        <>
            <p className="mb-3 text-center text-sm font-semibold text-[#4A4A4A] [text-wrap:balance]">{section.strip(tools.length)}</p>
            <ul role="list" className={`m-0 grid list-none grid-cols-2 gap-2.5 p-0 ${more ? 'lg:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]' : 'lg:grid-cols-4'}`}>
                {tools.map((t) => (
                    <li key={t.key} className="min-w-0">
                        <a
                            href={`#build-${t.key}`} data-cta={t.tileCta} data-build-tool={t.key}
                            className="flex h-full min-h-[44px] flex-col rounded-2xl border border-[#E0DED8] bg-white p-3.5 transition-colors hover:border-[#1A4D2E]/40"
                        >
                            <span className="font-serif text-lg font-bold leading-tight text-[#1A4D2E]">{t.name}</span>
                            <span className="mt-1 text-sm leading-snug text-[#4A4A4A]">{t.short}</span>
                            <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-[#1A4D2E]">See how <Arrow /></span>
                        </a>
                    </li>
                ))}
                {more && <MoreTile copy={more} />}
            </ul>
        </>
    );
    const box = 'mx-auto mt-4 max-w-5xl text-left sm:mt-5';
    return <div data-landing-section="build_strip" data-testid="landing-build-strip" className={box}>{body}</div>;
}

function ToolCard({ t, onStart }) {
    const Art = ARTIFACTS[t.key];
    return (
        <li id={`build-${t.key}`} data-testid={`landing-tool-${t.key}`} className="flex min-w-0 scroll-mt-24 flex-col rounded-2xl border border-[#E0DED8] bg-white p-4 transition-colors hover:border-[#1A4D2E]/40 sm:p-5">
            <div className="flex flex-col gap-4">
                {/* The artifact picture reads only at card width: phones get the words. */}
                <div data-replica className="relative hidden aspect-[4/3] w-full overflow-hidden rounded-xl border border-[#E0DED8] bg-white sm:block">
                    <Art />
                    <span className="sr-only">{BUILD_ARTIFACT_CAPTIONS[t.key]}</span>
                </div>
                <div className="min-w-0">
                    <h3 className="[text-wrap:balance] font-serif text-xl font-bold text-[#1A4D2E]">{t.name}</h3>
                    <p className="mt-1.5 text-base leading-relaxed text-[#4A4A4A] [text-wrap:pretty]">{t.line}</p>
                </div>
            </div>
            <p className="mt-3 border-l-2 border-[#9A7B2F] pl-3 text-sm leading-snug text-[#2C2C2C]">
                <span className="font-semibold">You walk away with: </span>{t.get}
            </p>
            {t.guide ? (
                <Link to={t.guide} data-cta={t.guideCta} className="mt-auto inline-flex min-h-[44px] items-center gap-1 pt-3 text-base font-semibold text-[#1A4D2E] hover:underline">
                    Read the guide <Arrow />
                </Link>
            ) : (
                <button type="button" onClick={() => onStart(t.startCta)} data-cta={t.startCta} className="mt-auto inline-flex min-h-[44px] items-center gap-1 pt-3 text-left text-base font-semibold text-[#1A4D2E] hover:underline">
                    {START_FREE} <Arrow />
                </button>
            )}
        </li>
    );
}

export function BuildCards({ tools, onStart, section = BUILD_SECTION, more = null }) {
    if (tools.length === 0) return null;
    return (
        <section id="build" data-landing-section="build" data-testid="landing-build" className="scroll-mt-20 bg-white px-4 py-10 sm:px-6 lg:px-8 lg:py-20">
            <div className="mx-auto max-w-6xl">
                <div className="mx-auto mb-8 max-w-3xl text-center">
                    <Eyebrow>{section.eyebrow}</Eyebrow>
                    <h2 className="[text-wrap:balance] mb-3 font-serif text-3xl font-bold leading-tight text-[#1A4D2E] sm:text-4xl lg:text-5xl">{section.title(tools.length)}</h2>
                    <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#4A4A4A]">{section.sub}</p>
                </div>
                <ul role="list" className={`m-0 grid list-none gap-4 p-0 sm:grid-cols-2 ${tools.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
                    {tools.map((t) => <ToolCard key={t.key} t={t} onStart={onStart} />)}
                    {more && <MoreTile copy={more} big />}
                </ul>
                {tools.some((t) => t.attorneyAside) && ATTORNEY && (
                    <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-[#E0DED8] bg-[#F5F8F5] p-5 text-center" data-testid="landing-attorney-estimate">
                        <p className="font-serif text-2xl font-bold text-[#1A4D2E]">{ATTORNEY.n}</p>
                        <p className="mx-auto mt-1 max-w-2xl text-base leading-relaxed text-[#2C2C2C] [text-wrap:balance]">
                            {ATTORNEY.line}. Here your student writes it, and your family files it with the USPTO and pays only the USPTO's own fee.
                        </p>
                        <Source>{ATTORNEY.source}</Source>
                    </div>
                )}
                <p className="mt-4 flex flex-wrap items-center justify-center gap-2 text-center text-base text-[#4A4A4A]">
                    <Badge>Part of the Season Pass</Badge>
                    <Link to="/pricing" data-cta="build_pricing" className="font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4">See what the pass includes</Link>
                </p>
                <div className="mt-6 flex justify-center">
                    <StartFree onStart={onStart} cta="build_start_free" size="md" />
                </div>
            </div>
        </section>
    );
}
